import dagre from '@dagrejs/dagre';
import { Node, Edge, Position } from 'reactflow';
import { WorkflowNodeData } from './WorkflowNode';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BuildIcon from '@mui/icons-material/Build';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import LoopIcon from '@mui/icons-material/Loop';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { StepType } from './WorkflowTypes';

const nodeWidth = 250;
const nodeHeight = 100;

/**
 * Layout nodes and edges using dagre for the top-level DAG, with a separate
 * topological sub-layout for loop-body (scoped) nodes rendered beside each loop node.
 *
 * Key design decisions:
 *  - Loop-body nodes are identified ONLY from `isLoopContainment` edge targets +
 *    their intra-body successors (edges between two already-scoped nodes, excluding
 *    loop-exit edges).  We do NOT propagate scoping past the loop-exit boundary, so
 *    nodes that come *after* a loop in the top-level DAG remain top-level.
 *  - Dagre is run only on the top-level nodes with a filtered edge set (no loop
 *    containment edges, no loop-exit edges, no intra-body edges).  This prevents
 *    dagre cycles and avoids body nodes skewing the horizontal/vertical shift.
 *  - Body nodes per loop are ordered by topological sort of their intra-body edges,
 *    then laid out in a vertical column to the right of their parent loop node.
 *    Multiple parallel branches inside a loop body are stacked vertically with extra
 *    spacing to keep them distinct.
 *  - A final overlap-resolution pass nudges any two nodes that happen to land on the
 *    same grid cell, spreading them out horizontally (not just vertically) to avoid
 *    long single-column stacks.
 */
export const getLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  targetY = 250,
  startX = 250,
  ranksep = 200,
  nodesep = 120,
) => {
  if (nodes.length === 0) return { nodes, edges };

  // ── Step 1: Identify all loop-body nodes ────────────────────────────────────
  // First pass: direct entry-body nodes from isLoopContainment edges.
  // Second pass: propagate to intra-body successors ONLY (edges where BOTH
  //   source AND target are within the same loop body, and not loop-exit edges).
  //   We stop at the loop-exit boundary so post-loop nodes stay top-level.
  const loopBodyGroups: Record<string, Set<string>> = {}; // loopId → body node ids
  edges.forEach((e) => {
    if ((e.data as any)?.isLoopContainment) {
      if (!loopBodyGroups[e.source]) loopBodyGroups[e.source] = new Set();
      loopBodyGroups[e.source].add(e.target);
    }
  });

  // Propagate: if a node is in a loop body and has an outgoing edge to another
  // node that is not the loop node itself and not a loop-exit edge, that target
  // also belongs to the same body. Use a for..of loop to avoid closure-over-loop-var lint.
  let propagated = true;
  while (propagated) {
    propagated = false;
    for (const e of edges) {
      if ((e.data as any)?.isLoopContainment || (e.data as any)?.isLoopExit) continue;
      for (const [loopId, bodySet] of Object.entries(loopBodyGroups)) {
        if (bodySet.has(e.source) && !bodySet.has(e.target) && e.target !== loopId) {
          bodySet.add(e.target);
          propagated = true;
        }
      }
    }
  }

  // Flat set of all scoped node ids (union across all loops)
  const scopedNodeIds = new Set<string>();
  for (const bodySet of Object.values(loopBodyGroups)) {
    bodySet.forEach((id) => scopedNodeIds.add(id));
  }

  const topLevelNodes = nodes.filter((n) => !scopedNodeIds.has(n.id));
  const nodeById = new Map(nodes.map((n) => [n.id, n]));

  // ── Step 2: Run dagre on top-level nodes only ────────────────────────────────
  // Filter edges to top-level structural edges only (no loop containment, no body
  // edges, no loop-exit edges, no pure data-flow edges).
  const topLevelNodeIds = new Set(topLevelNodes.map((n) => n.id));
  const dagreEdges = edges.filter((e) =>
    topLevelNodeIds.has(e.source) &&
    topLevelNodeIds.has(e.target) &&
    !(e.data as any)?.isLoopContainment &&
    !(e.data as any)?.isLoopExit &&
    !(e.data as any)?.isDataFlow,
  );

  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'LR', ranksep, nodesep });

  topLevelNodes.forEach((node) => {
    g.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });
  dagreEdges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  dagre.layout(g);

  // Compute shift so that the leftmost top-level node starts at startX and
  // the graph is vertically centered around targetY.
  let minY = Infinity, maxY = -Infinity, minX = Infinity;
  topLevelNodes.forEach((node) => {
    const dn = g.node(node.id);
    if (!dn) return;
    minY = Math.min(minY, dn.y);
    maxY = Math.max(maxY, dn.y);
    minX = Math.min(minX, dn.x);
  });
  if (!isFinite(minY)) { minY = targetY; maxY = targetY; minX = startX; }

  const dagreCenterY = minY + (maxY - minY) / 2;
  const verticalShift = targetY - dagreCenterY;
  const horizontalShift = startX - (minX - nodeWidth / 2);

  // ── Step 3: Position top-level nodes from dagre ──────────────────────────────
  const topLevelPositions: Record<string, { x: number; y: number }> = {};
  const layoutedTopLevel: Node[] = topLevelNodes.map((node) => {
    const dn = g.node(node.id);
    const pos = dn
      ? { x: dn.x - nodeWidth / 2 + horizontalShift, y: dn.y - nodeHeight / 2 + verticalShift }
      : { x: startX, y: targetY };
    topLevelPositions[node.id] = pos;
    return { ...node, targetPosition: Position.Left, sourcePosition: Position.Right, position: pos };
  });

  // ── Step 4: Topological sort of body nodes per loop ──────────────────────────
  // Produces an ordered list that respects intra-body depends_on so the column
  // flows naturally top-to-bottom.
  const topoSort = (bodyIds: string[]): string[] => {
    const bodySet = new Set(bodyIds);
    const inDegree: Record<string, number> = {};
    const adj: Record<string, string[]> = {};
    bodyIds.forEach((id) => { inDegree[id] = 0; adj[id] = []; });
    edges.forEach((e) => {
      if (
        bodySet.has(e.source) &&
        bodySet.has(e.target) &&
        !(e.data as any)?.isLoopContainment &&
        !(e.data as any)?.isLoopExit &&
        !(e.data as any)?.isDataFlow
      ) {
        adj[e.source].push(e.target);
        inDegree[e.target] = (inDegree[e.target] ?? 0) + 1;
      }
    });
    const queue = bodyIds.filter((id) => inDegree[id] === 0);
    const result: string[] = [];
    while (queue.length) {
      const cur = queue.shift()!;
      result.push(cur);
      for (const next of (adj[cur] || [])) {
        inDegree[next]--;
        if (inDegree[next] === 0) queue.push(next);
      }
    }
    // Append any nodes that weren't reachable (disconnected within body)
    bodyIds.forEach((id) => { if (!result.includes(id)) result.push(id); });
    return result;
  };

  // ── Step 5: Position loop-body nodes ────────────────────────────────────────
  // Each loop's body is laid out in a vertical column to the right of the loop node.
  // Enough horizontal space is reserved so the column doesn't overlap the next
  // top-level column; the body column sits BETWEEN the loop node column and the
  // next top-level column.
  const BODY_OFFSET_X = nodeWidth + 70;   // right of loop node
  const BODY_SPACING_Y = nodeHeight + 30; // vertical gap between body nodes

  const layoutedScoped: Node[] = [];
  for (const [loopId, bodySet] of Object.entries(loopBodyGroups)) {
    const loopPos = topLevelPositions[loopId] ?? { x: startX, y: targetY };
    const orderedBody = topoSort(Array.from(bodySet));
    // Center the body column vertically around the loop node's y
    const totalBodyHeight = (orderedBody.length - 1) * BODY_SPACING_Y;
    const bodyStartY = loopPos.y - totalBodyHeight / 2;

    orderedBody.forEach((bodyId, idx) => {
      const node = nodeById.get(bodyId);
      if (!node) return;
      layoutedScoped.push({
        ...node,
        targetPosition: Position.Left,
        sourcePosition: Position.Right,
        position: {
          x: loopPos.x + BODY_OFFSET_X,
          y: bodyStartY + idx * BODY_SPACING_Y,
        },
      });
    });
  }

  // ── Step 6: Fallback for any nodes not yet assigned ───────────────────────────
  const assignedIds = new Set([
    ...layoutedTopLevel.map((n) => n.id),
    ...layoutedScoped.map((n) => n.id),
  ]);
  let fallbackCol = 0;
  let fallbackRow = 0;
  const fallbackNodes: Node[] = nodes
    .filter((n) => !assignedIds.has(n.id))
    .map((node) => {
      const pos = {
        x: startX + fallbackCol * (nodeWidth + 80),
        y: targetY + 500 + fallbackRow * (nodeHeight + 50),
      };
      fallbackCol++;
      if (fallbackCol >= 4) { fallbackCol = 0; fallbackRow++; }
      return { ...node, targetPosition: Position.Left, sourcePosition: Position.Right, position: pos };
    });

  // ── Step 7: Overlap resolution ────────────────────────────────────────────────
  // Build a grid-snapped position set and resolve any collisions by nudging
  // horizontally first (to avoid long vertical stacks), then vertically.
  const GRID = 20; // snap resolution in px
  const snap = (v: number) => Math.round(v / GRID) * GRID;
  const occupied = new Set<string>();
  const key = (x: number, y: number) => `${snap(x)},${snap(y)}`;

  const allNodes: Node[] = [...layoutedTopLevel, ...layoutedScoped, ...fallbackNodes];
  allNodes.forEach((node) => {
    let { x, y } = node.position;
    // Try up to 20 columns before wrapping to next row
    let attempts = 0;
    while (occupied.has(key(x, y)) && attempts < 40) {
      x += GRID * 2;
      if (attempts > 0 && attempts % 20 === 0) { x = node.position.x; y += GRID * 2; }
      attempts++;
    }
    occupied.add(key(x, y));
    node.position = { x, y };
  });

  return { nodes: allNodes, edges };
};
export const sentenceCaseWord = (word:any) => {
  if (!word) {
    return "";
  }
  // Capitalize the first letter and concatenate with the rest of the word in lowercase
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/**
 * Create a new workflow node with consistent structure
 * @param selectedType - Type of node: 'agent', 'tool', or 'trigger'
 * @param formData - Form data containing node configuration
 * @param label - Display label for the node
 * @param existingNodesCount - Number of existing nodes (for positioning)
 * @returns A new Node<WorkflowNodeData> object
 */

export const createWorkflowNode = (
  selectedType: string,
  formData: any,
  label: string,
  existingNodesCount: number
): Node<WorkflowNodeData> => {
  const id = `${selectedType}-${Date.now()}`;

  // horizontal spacing: ~250px per node
  // vertical center: usually 200-300px depending on container size
  const xOffset = 50 + (existingNodesCount * 250);
  const yCenter = 250;

  // Extract tool_parameters and input_mapping for later reference
  const { tool_parameters, input_mapping, ...configData } = formData;

  return {
    id,
    type: "muiNode",
    data: {
      label: label,
      type: selectedType as StepType | "trigger",
      config: { ...configData, input_mapping },
      rawData: {
        tool_parameters,
        input_mapping,
      },
    },
    // Set source/target positions for horizontal flow
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    position: { x: xOffset, y: yCenter },
  };
};

/**
 * Default cron expression for schedule triggers
 * Format: minute hour day-of-month month day-of-week
 * "0 9 * * *" = Every day at 9:00 AM
 */
export const DEFAULT_CRON_EXPRESSION = "0 9 * * *";

/**
 * Helper text to explain the default cron expression
 */
export const CRON_HELPER_TEXT = `Default: Daily at 9:00 AM (${DEFAULT_CRON_EXPRESSION})`;

/**
 * Placeholder text for cron input field
 */
export const CRON_PLACEHOLDER = DEFAULT_CRON_EXPRESSION;

/**
 * Node type options for the add node popover
 */
export const nodeTypeOptions = [
  {
    category: "Step",
    options: [
      {
        id: "agent",
        label: "Agent",
        icon: SmartToyIcon,
        // color: "#F59E0B", // Amber/Bronze
        color: "#B87333", // Theme Bronze
        description: "AI decision maker",
      },
      {
        id: "tool",
        label: "Tool",
        icon: BuildIcon,
        // color: "#450A0A",
        // color: "#9F522F", // Deep Clay/Terracotta
        color: "#789769", // Sophisticated Muted Sage
        description: "External API or function",
      },
      {
        id: "human_input",
        label: "Human Approval",
        icon: PersonOutlineIcon,
        color: "#8E588E",
        description: "Manual review / approval step",
      },
      {
        id: "conditional",
        label: "Conditional",
        icon: AltRouteIcon,
        color: "#D4A017",
        description: "Route based on conditions",
      },
      {
        id: "loop",
        label: "Loop",
        icon: LoopIcon,
        color: "#4A90D9",
        description: "Iterate over items or conditions",
      },
      {
        id: "merge",
        label: "Merge",
        icon: CallMergeIcon,
        color: "#2E8B57",
        description: "Merge parallel branches",
      },
      {
        id: "wait",
        label: "Wait",
        icon: HourglassEmptyIcon,
        color: "#6B7B8D",
        description: "Delay execution",
      },
    ],
  },
  {
    category: "Trigger",
    options: [
      {
        id: "schedule",
        label: "Schedule",
        icon: ScheduleIcon,
        // color: "#64748B", // Slate/Charcoal
        // color: "#94A3B8", // Light Silver Slate
        color: "#5A7D9A", // Calm Steel Blue
        description: "Run on a time basis",
      },
      {
        id: "webhook",
        label: "Webhook",
        icon: CloudQueueIcon,
        //  color: "#94A3B8", // Light Silver Slate
        color: "#2C3E50", // Deep Ocean Blue
        description: "Start via external API call",
      },
    ],
  },
  {
    category: "Layout",
    options: [
      {
        id: "auto-layout",
        label: "Auto Layout",
        icon: ViewQuiltIcon,
        color: "#60A5FA",
        description: "Arrange nodes automatically",
      },
    ],
  },
];

/**
 * Default labels for structural (non-agent, non-tool) node types.
 * Used when adding a new node to give it a meaningful initial label.
 */
export const STRUCTURAL_NODE_DEFAULT_LABELS: Record<string, string> = {
  merge: 'Merge Results',
  conditional: 'Route',
  loop: 'Loop',
  wait: 'Wait',
  human_input: 'Human Review',
};

/**
 * Generate a unique slug from a node label
 */
const STEP_ID_MAX_LENGTH = 512;

export const generateSlug = (label: string, usedSlugs: Set<string>): string => {
  let slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  if (!slug) slug = 'step';

  // Truncate to leave room for uniqueness suffix (e.g. "_99")
  if (slug.length > STEP_ID_MAX_LENGTH - 3) {
    slug = slug.substring(0, STEP_ID_MAX_LENGTH - 3).replace(/_+$/, '');
  }

  let uniqueSlug = slug;
  let counter = 1;
  while (usedSlugs.has(uniqueSlug)) {
    uniqueSlug = `${slug}_${counter}`;
    counter++;
  }
  return uniqueSlug;
};

/**
 * Convert nodes and edges to workflow DAG steps
 * @param nodes - Workflow nodes
 * @param edges - Workflow edges (optional, for determining trigger connections)
 */
export const convertNodesToSteps = (nodes: Node<WorkflowNodeData>[], edges?: Edge[]): any[] => {
  // Generate Slugs for Steps (Map Node ID -> Slug)
  const nodeIdToSlug: Record<string, string> = {};
  const usedSlugs = new Set<string>();

  nodes.forEach((node) => {
    const uniqueSlug = generateSlug(node.data.label, usedSlugs);
    usedSlugs.add(uniqueSlug);
    nodeIdToSlug[node.id] = uniqueSlug;
  });

  // Separate Triggers from Action Steps
  const actionNodes = nodes.filter((node) => node.data.type !== 'trigger');

  // Build dependency map from edges
  const dependencyMap: Record<string, string[]> = {};
  if (edges) {
    actionNodes.forEach(node => {
      dependencyMap[node.id] = [];
    });

    edges.forEach(edge => {
      // Only consider structural edges (not data flow or trigger edges)
      const triggerNodeIds = new Set(nodes.filter(n => n.data.type === 'trigger').map(n => n.id));
      if (!triggerNodeIds.has(edge.source) && !(edge.data as any)?.isDataFlow && dependencyMap[edge.target] !== undefined) {
        dependencyMap[edge.target].push(edge.source);
      }
    });
  }

  // Helper function to translate all node ID references in a string
  const translateNodeIds = (str: string): string => {
    if (!str) return str;
    let result = str;
    // Replace all occurrences of node IDs with their slugs
    for (const [nodeId, slug] of Object.entries(nodeIdToSlug)) {
      // Replace in JSONPath expressions: $.steps.{nodeId}.output -> $.steps.{slug}.output
      result = result.replace(new RegExp(`\\$\\.steps\\.${nodeId}`, 'g'), `$.steps.${slug}`);
      // Replace in template variables: {{nodeId_output}} -> {{slug_output}}
      result = result.replace(new RegExp(nodeId, 'g'), slug);
    }
    return result;
  };

  // Helper function to translate node IDs in objects (recursive)
  const translateNodeIdsInObject = (obj: any): any => {
    if (!obj) return obj;
    if (typeof obj === 'string') return translateNodeIds(obj);
    if (Array.isArray(obj)) return obj.map(translateNodeIdsInObject);
    if (typeof obj === 'object') {
      const result: any = {};
      for (const [key, value] of Object.entries(obj)) {
        result[key] = translateNodeIdsInObject(value);
      }
      return result;
    }
    return obj;
  };

  // Convert Action Nodes to DAG Definition Steps
  const steps = actionNodes.map((node) => {
    const stepId = nodeIdToSlug[node.id];

    // Translate dependencies - use edges if available, fallback to config
    const originalDependsOn = edges ? (dependencyMap[node.id] || []) : (node.data.config.depends_on || []);
    const translatedDependsOn = originalDependsOn
      .map((depId) => nodeIdToSlug[depId])
      .filter(Boolean);

    // Translate input mappings
    // Check both rawData and config for input_mapping (could be in either after edits)
    const nodeInputMapping = node.data.rawData?.input_mapping || node.data.config.input_mapping || {};
    // Strip legacy auto-injected "json_data":"$" (bug artifact that passes entire execution
    // context as an extra kwarg, breaking tool functions)
    const { json_data: _legacyJsonData, ...cleanedMapping } = nodeInputMapping;
    const originalMapping = { ...cleanedMapping };
    const translatedMapping = translateNodeIdsInObject(originalMapping);

    const baseStep: any = {
      id: stepId,
      type: node.data.type,
      depends_on: translatedDependsOn,
      input_mapping: translatedMapping,
      description: node.data.label,
      timeout_seconds: node.data.rawData?.timeout_seconds || node.data.config?.timeout_seconds || 300,
      ...(node.data.config.on_failure ? { on_failure: node.data.config.on_failure } : {}),
    };

    if (node.data.type === 'agent') {
      // Check both rawData and config for prompt
      const prompt = node.data.rawData?.prompt || node.data.config.prompt || '';
      return {
        ...baseStep,
        agent_id: node.data.config.id,
        prompt: translateNodeIds(prompt),
        agent_version: parseInt(node.data.config.version || '1', 10),
      };
    } else if (node.data.type === 'tool') {
      return {
        ...baseStep,
        tool_id: node.data.config.id,
        tool_version: parseInt(node.data.config.version || '1', 10),
      };
    } else if (node.data.type === 'conditional') {
      const routingConfig = node.data.rawData?.routing_config || node.data.config.routing_config;
      const normalizedConfig = routingConfig ? {
        ...routingConfig,
        branches: (routingConfig.branches || []).map((branch: any) => ({
          ...branch,
          next_steps: branch.next_steps || [],
        })),
      } : routingConfig;
      return {
        ...baseStep,
        routing_config: translateNodeIdsInObject(normalizedConfig),
      };
    } else if (node.data.type === 'human_input') {
      const prompt = node.data.rawData?.prompt || node.data.config.prompt || '';
      const dataToDisplay = node.data.rawData?.data_to_display || node.data.config.data_to_display;
      return {
        ...baseStep,
        title: node.data.config.title || node.data.label,
        prompt: translateNodeIds(prompt),
        data_to_display: translateNodeIdsInObject(dataToDisplay),
        input_fields: node.data.config.input_fields,
        timeout_minutes: node.data.config.timeout_minutes,
        on_timeout: node.data.config.on_timeout || 'fail',
      };
    } else if (node.data.type === 'loop') {
      const loopConfig = node.data.rawData?.loop_config || node.data.config.loop_config;
      return {
        ...baseStep,
        loop_config: translateNodeIdsInObject(loopConfig),
        loop_body_steps: node.data.config.loop_body_steps,
        output_mode: node.data.config.output_mode || 'collect',
      };
    } else if (node.data.type === 'merge') {
      return {
        ...baseStep,
        merge_strategy: node.data.config.merge_strategy || 'all',
      };
    } else if (node.data.type === 'wait') {
      const waitConfig = node.data.rawData?.wait_config || node.data.config.wait_config;
      return {
        ...baseStep,
        wait_config: translateNodeIdsInObject(waitConfig),
      };
    } else {
      return baseStep;
    }
  });

  return steps;
};

/**
 * Get trigger nodes from workflow
 */
export const getTriggerNodes = (nodes: Node<WorkflowNodeData>[]): Node<WorkflowNodeData>[] => {
  return nodes.filter((node) => node.data.type === 'trigger');
};

/**
 * Sanitize workflow name to match backend schema requirements
 * Pattern: ^[a-z0-9][a-z0-9-]*[a-z0-9]$
 * - Must start with lowercase letter or number
 * - Can contain lowercase letters, numbers, and hyphens
 * - Must end with lowercase letter or number
 * - No spaces, uppercase letters, or special characters
 */
export const sanitizeWorkflowName = (name: string): string => {
  // Convert to lowercase and replace spaces with hyphens
  let sanitized = name.toLowerCase().replace(/\s+/g, '-');
  
  // Remove any characters that aren't lowercase letters, numbers, or hyphens
  sanitized = sanitized.replace(/[^a-z0-9-]/g, '');
  
  // Remove leading/trailing hyphens
  sanitized = sanitized.replace(/^-+|-+$/g, '');
  
  // Replace multiple consecutive hyphens with a single hyphen
  sanitized = sanitized.replace(/-+/g, '-');
  
  // Ensure minimum length of 3 characters
  if (sanitized.length < 3) {
    sanitized = `workflow-${Date.now().toString().slice(-6)}`;
  }
  
  // Truncate to max 512 characters
  if (sanitized.length > 512) {
    sanitized = sanitized.substring(0, 512);
  }
  
  // Ensure it doesn't end with a hyphen (after truncation)
  sanitized = sanitized.replace(/-+$/, '');
  
  return sanitized;
};

/**
 * Handle node type selection and return updated form data
 * Centralizes the logic for setting default values when a node type is selected
 */
export const handleNodeTypeSelection = (
  optionId: string,
  prevFormData: {
    id: string;
    version: string;
    prompt: string;
    triggerType: 'schedule' | 'webhook';
    cron: string;
    webhookConfig: {
      adapter_type: string;
      secrets: Record<string, string>;
    };
  }
): {
  selectedType: 'agent' | 'tool' | 'trigger';
  formData: typeof prevFormData;
} => {
  if (optionId === 'schedule' || optionId === 'webhook') {
    return {
      selectedType: 'trigger',
      formData: {
        ...prevFormData,
        triggerType: optionId as 'schedule' | 'webhook',
        id: optionId, // Set a placeholder ID
        // Set default cron for schedule trigger using constant
        cron: optionId === 'schedule' ? DEFAULT_CRON_EXPRESSION : '',
      },
    };
  } else {
    return {
      selectedType: optionId as 'agent' | 'tool',
      formData: { ...prevFormData, id: '' },
    };
  }
};

/**
 * Extract step ID references from input_mapping values.
 * Parses JSONPath expressions like "$.steps.step_id.output" to extract "step_id".
 */
const extractStepRefsFromMapping = (mapping: Record<string, string> | undefined): string[] => {
  if (!mapping) return [];
  const refs = new Set<string>();
  const pattern = /\$\.steps\.([a-zA-Z0-9_-]+)/g;
  for (const value of Object.values(mapping)) {
    if (typeof value !== 'string') continue;
    let match;
    while ((match = pattern.exec(value)) !== null) {
      refs.add(match[1]);
    }
  }
  return Array.from(refs);
};

/**
 * Load workflow nodes and edges from workflow data, including schedule trigger if configured
 * 
 * @param workflow - Workflow data with dag_definition
 * @param scheduleConfig - Optional schedule configuration from API
 * @param edgeType - Edge type for React Flow ('default' or 'smoothstep')
 * @param markerType - Marker type for edges (default: MarkerType.ArrowClosed)
 * @returns Object with nodes and edges arrays ready for React Flow
 */
export const loadWorkflowNodesWithTriggers = async (
  workflow: any,
  edgeType: string = 'default',
  markerType: any = { type: 'arrowclosed' },
  show_status: boolean = false
): Promise<{ nodes: Node<WorkflowNodeData>[]; edges: Edge[] }> => {
  if (!workflow?.dag_definition?.steps) {
    return { nodes: [], edges: [] };
  }

  // Create nodes from workflow steps
  const initialNodes: Node<WorkflowNodeData>[] = workflow.dag_definition.steps.map((step: any) => {
    const baseConfig: any = {
      id: step.type === 'agent' ? step.agent_id : (step.type === 'tool' ? step.tool_id : step.id),
      version: step.agent_version ? String(step.agent_version) : (step.tool_version ? String(step.tool_version) : '1'),
      prompt: step.prompt,
      depends_on: step.depends_on,
      input_mapping: step.input_mapping,
      on_failure: step.on_failure,
      timeout_seconds: step.timeout_seconds,
    };

    // Add type-specific config
    if (step.type === 'conditional') {
      baseConfig.routing_config = step.routing_config;
    } else if (step.type === 'human_input') {
      baseConfig.title = step.title;
      baseConfig.data_to_display = step.data_to_display;
      baseConfig.input_fields = step.input_fields;
      baseConfig.timeout_minutes = step.timeout_minutes;
      baseConfig.on_timeout = step.on_timeout;
    } else if (step.type === 'loop') {
      baseConfig.loop_config = step.loop_config;
      baseConfig.loop_body_steps = step.loop_body_steps;
      baseConfig.output_mode = step.output_mode;
    } else if (step.type === 'merge') {
      baseConfig.merge_strategy = step.merge_strategy;
    } else if (step.type === 'wait') {
      baseConfig.wait_config = step.wait_config;
    }

    return {
      id: step.id,
      type: 'muiNode',
      data: {
        label: step.id || step.title || step.description,
        type: step.type,
        show_status: show_status,
        config: baseConfig,
      },
      position: { x: 0, y: 0 },
    };
  });

  // Create edges from dependencies (solid) and input_mapping data flow (dotted)
  const initialEdges: Edge[] = [];
  const scopedStepIds = new Set<string>(
    workflow.dag_definition.steps
      .filter((s: any) => s.scope)
      .map((s: any) => s.id)
  );

  workflow.dag_definition.steps.forEach((step: any) => {
    // For scoped body steps, only draw edges to other body steps in the same scope
    if (step.scope) {
      const dependsOnSet = new Set<string>(step.depends_on || []);
      (step.depends_on || []).forEach((depId: string) => {
        const depStep = workflow.dag_definition.steps.find((s: any) => s.id === depId);
        if (depStep && depStep.scope === step.scope) {
          initialEdges.push({
            id: `dep-${depId}-${step.id}`,
            source: depId,
            target: step.id,
            type: edgeType,
            markerEnd: markerType,
          });
        }
      });
      // Also internal data edges
      const inputMapping: Record<string, string> = step.input_mapping || {};
      const dataFlowRefs = extractStepRefsFromMapping(inputMapping);
      dataFlowRefs.forEach((refId: string) => {
        if (dependsOnSet.has(refId)) return;
        const refStep = workflow.dag_definition.steps.find((s: any) => s.id === refId);
        if (refStep && refStep.scope === step.scope) {
          initialEdges.push({
            id: `data-${refId}-${step.id}`,
            source: refId,
            target: step.id,
            type: edgeType,
            markerEnd: markerType,
            style: { strokeDasharray: '5 5', stroke: '#94A3B8', strokeWidth: 1.5 },
            label: 'data',
            labelStyle: { fontSize: 8, fill: '#94A3B8' },
            labelBgStyle: { fill: 'white', fillOpacity: 0.8 },
            data: { isDataFlow: true },
          });
        }
      });
      return;
    }

    const dependsOnSet = new Set<string>(step.depends_on || []);

    // Solid edges for depends_on
    if (step.depends_on) {
      step.depends_on.forEach((depId: string) => {
        if (scopedStepIds.has(depId)) return; // don't draw edge to a body step
        initialEdges.push({
          id: `dep-${depId}-${step.id}`,
          source: depId,
          target: step.id,
          type: edgeType,
          markerEnd: markerType,
        });
      });
    }

    // Dotted edges for input_mapping data flow (only refs NOT already in depends_on)
    const inputMapping: Record<string, string> = step.input_mapping || {};
    const dataFlowRefs = extractStepRefsFromMapping(inputMapping);
    dataFlowRefs.forEach((refId: string) => {
      if (scopedStepIds.has(refId)) return; // don't draw data edge to a body step
      if (!dependsOnSet.has(refId)) {
        initialEdges.push({
          id: `data-${refId}-${step.id}`,
          source: refId,
          target: step.id,
          type: edgeType,
          markerEnd: markerType,
          style: { strokeDasharray: '5 5', stroke: '#94A3B8', strokeWidth: 1.5 },
          label: 'data',
          labelStyle: { fontSize: 8, fill: '#94A3B8' },
          labelBgStyle: { fill: 'white', fillOpacity: 0.8 },
          data: { isDataFlow: true },
        });
      }
    });
  });


  // Add loop containment edges: loop node → body entry steps (indigo, for grouping)
  // Also add a special 'exit' edge from the last body step to the loop node (dashed, labeled 'exit')
  const loopContainmentEdges: Edge[] = [];
  workflow.dag_definition.steps
    .filter((step: any) => step.type === 'loop')
    .forEach((loopStep: any) => {
      const bodyIds: string[] = loopStep.loop_body_steps || [];
      const bodyIdSet = new Set(bodyIds);

      // Entry steps: body steps with no in-body deps
      const entrySteps = bodyIds.filter((bsId: string) => {
        const bs = workflow.dag_definition.steps.find((s: any) => s.id === bsId);
        if (!bs) return false;
        return !(bs.depends_on || []).some((dep: string) => bodyIdSet.has(dep));
      });

      entrySteps.forEach((entryId: string) => {
        loopContainmentEdges.push({
          id: `loop-entry-${loopStep.id}-${entryId}`,
          source: loopStep.id,
          target: entryId,
          type: edgeType,
          markerEnd: markerType,
          style: { stroke: '#6366F1', strokeWidth: 2 },
          label: 'body',
          labelStyle: { fontSize: 9, fill: '#6366F1', fontWeight: 600 },
          labelBgStyle: { fill: 'white', fillOpacity: 0.9 },
          data: { isLoopContainment: true },
        });
      });

      // Add an 'exit' edge from the last body step to the loop node
      // Find last body step: the one not depended on by any other body step
      let lastBodyStepId = null;
      for (const bsId of bodyIds) {
        const isDependedOn = bodyIds.some((otherId) => {
          if (otherId === bsId) return false;
          const otherStep = workflow.dag_definition.steps.find((s: any) => s.id === otherId);
          return (otherStep?.depends_on || []).includes(bsId);
        });
        if (!isDependedOn) {
          lastBodyStepId = bsId;
        }
      }
      if (lastBodyStepId) {
        loopContainmentEdges.push({
          id: `loop-exit-${lastBodyStepId}-${loopStep.id}`,
          source: lastBodyStepId,
          target: loopStep.id,
          type: edgeType,
          markerEnd: markerType,
          style: { stroke: '#6366F1', strokeDasharray: '6 4', strokeWidth: 2 },
          label: 'exit',
          labelStyle: { fontSize: 9, fill: '#6366F1', fontWeight: 600 },
          labelBgStyle: { fill: 'white', fillOpacity: 0.9 },
          data: { isLoopExit: true },
        });
      }
    });

  // Find nodes with no dependencies (start nodes) - used for trigger connections
  // If a loop has no dependencies, use its entry body step as the start node
  let startNodes: any[] = [];
  workflow.dag_definition.steps.forEach((step: any) => {
    if (step.type === 'loop' && (!step.depends_on || step.depends_on.length === 0)) {
      // Use entry body step(s) as start
      const bodyIds: string[] = step.loop_body_steps || [];
      const bodyIdSet = new Set(bodyIds);
      const entrySteps = bodyIds.filter((bsId: string) => {
        const bs = workflow.dag_definition.steps.find((s: any) => s.id === bsId);
        if (!bs) return false;
        return !(bs.depends_on || []).some((dep: string) => bodyIdSet.has(dep));
      });
      startNodes.push(...entrySteps.map((id) => workflow.dag_definition.steps.find((s: any) => s.id === id)));
    } else if ((!step.depends_on || step.depends_on.length === 0) && !step.scope) {
      startNodes.push(step);
    }
  });

  // Add schedule trigger node if config exists in workflow response
  const scheduleConfig = workflow.schedule_config;
  if (scheduleConfig && scheduleConfig.enabled && scheduleConfig.cron_expression) {
    const triggerNode: Node<WorkflowNodeData> = {
      id: 'schedule-trigger',
      type: 'muiNode',
      data: {
        label: 'Schedule Trigger',
        type: 'trigger',
        show_status: show_status,
        config: {
          id: 'schedule-trigger',
          version: '1',
          triggerType: 'schedule',
          cron: scheduleConfig.cron_expression,
          depends_on: [],
          input_mapping: {}
        }
      },
      position: { x: 0, y: 0 }
    };

    initialNodes.push(triggerNode);

    // Connect trigger to all start nodes
    startNodes.forEach((step: any) => {
      initialEdges.push({
        id: `schedule-trigger-${step.id}`,
        source: 'schedule-trigger',
        target: step.id,
        type: edgeType,
        markerEnd: markerType,
      });
    });
  }

  // Add webhook trigger node if config exists in workflow response
  const webhookConfig = workflow.webhook_config;
  if (webhookConfig && webhookConfig.enabled && webhookConfig.adapter_type) {
    // Generate label based on adapter type
    const getWebhookLabel = (adapterType: string): string => {
      const labels: Record<string, string> = {
        'slack': 'Slack Webhook',
        'zendesk': 'Zendesk Webhook',
        'generic': 'Webhook Trigger'
      };
      return labels[adapterType] || 'Webhook Trigger';
    };

    const triggerNode: Node<WorkflowNodeData> = {
      id: 'webhook-trigger',
      type: 'muiNode',
      data: {
        label: getWebhookLabel(webhookConfig.adapter_type),
        type: 'trigger',
        config: {
          id: 'webhook-trigger',
          version: '1',
          triggerType: 'webhook',
          webhookConfig: {
            adapter_type: webhookConfig.adapter_type,
            secrets: {} // Secrets are redacted in response, don't show them
          },
          depends_on: [],
          input_mapping: {}
        }
      },
      position: { x: 0, y: 0 }
    };

    initialNodes.push(triggerNode);

    // Connect trigger to all start nodes
    startNodes.forEach((step: any) => {
      initialEdges.push({
        id: `webhook-trigger-${step.id}`,
        source: 'webhook-trigger',
        target: step.id,
        type: edgeType,
        markerEnd: markerType,
      });
    });
  }

  const allEdges = [...initialEdges, ...loopContainmentEdges];
  return { nodes: initialNodes, edges: allEdges };
};

/**
 * Compute dotted "data flow" edges from nodes' input_mapping.
 * Returns edges for input_mapping step references that are NOT already
 * covered by an existing edge (i.e. implicit data dependencies).
 * Used by the workflow generator where edges are built interactively.
 */
export const computeDataFlowEdges = (
  nodes: Node<WorkflowNodeData>[],
  existingEdges: Edge[],
  edgeType: string = 'default',
  markerType: any = { type: 'arrowclosed' },
): Edge[] => {
  const existingPairs = new Set(existingEdges.map(e => `${e.source}→${e.target}`));
  const nodeIds = new Set(nodes.map(n => n.id));
  const dataEdges: Edge[] = [];

  nodes.forEach(node => {
    if (node.data.type === 'trigger') return;
    const mapping: Record<string, string> = node.data.type === 'conditional'
      ? (node.data.rawData?.routing_config?.input_mapping || node.data.config?.routing_config?.input_mapping || {})
      : (node.data.rawData?.input_mapping || node.data.config?.input_mapping || {});

    const refs = extractStepRefsFromMapping(mapping);
    refs.forEach(refId => {
      if (!nodeIds.has(refId)) return; // ref must exist as a node
      if (existingPairs.has(`${refId}→${node.id}`)) return; // already an edge

      dataEdges.push({
        id: `data-${refId}-${node.id}`,
        source: refId,
        target: node.id,
        type: edgeType,
        markerEnd: markerType,
        style: { strokeDasharray: '5 5', stroke: '#94A3B8', strokeWidth: 1.5 },
        label: 'data',
        labelStyle: { fontSize: 8, fill: '#94A3B8' },
        labelBgStyle: { fill: 'white', fillOpacity: 0.8 },
        data: { isDataFlow: true },
      });
    });
  });

  return dataEdges;
};
